import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ShieldCheck } from 'lucide-react';
import { LogoBox, useTenantBrand } from '../context/TenantBrandContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ageOf = (dob) => {
  if (!dob) return null;
  const d = new Date(dob), n = new Date();
  return n.getFullYear() - d.getFullYear() - (n < new Date(n.getFullYear(), d.getMonth(), d.getDate()) ? 1 : 0);
};

/** Sign up or sign in wearing the organization's brand. `mode` is set by the route. */
export default function TenantAuthPage({ mode = 'join' }) {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const { tenant, palette, loadBySlug } = useTenantBrand();
  const [f, setF] = useState({ full_name: '', email: '', password: '', date_of_birth: '',
                               code: (params.get('code') || '').toUpperCase() });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!tenant || tenant.slug !== slug) loadBySlug(slug).catch(() => {}); },
    [slug, tenant, loadBySlug]);

  const P = palette;
  const b = tenant?.branding || {};
  const name = tenant?.name || 'FuturePath';
  const joining = mode === 'join';

  const submit = async () => {
    setBusy(true);
    try {
      if (joining) {
        const r = await axios.post(`${API}/auth/register`, {
          email: f.email.trim().toLowerCase(), full_name: f.full_name.trim(),
          password: f.password, role: 'student',
          date_of_birth: f.date_of_birth || undefined });
        const token = r.data.access_token;
        localStorage.setItem('futurepath_token', token);
        if (f.code) {
          await axios.post(`${API}/identity/redeem-code`,
            { code: f.code.trim().toUpperCase(), age: ageOf(f.date_of_birth) || 16 },
            { headers: { Authorization: `Bearer ${token}` } }).catch((e) => {
              toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail
                || "We couldn't attach you to that code, your account is made, tell a coordinator.");
            });
        }
        window.location.assign('/app/welcome');
      } else {
        const res = await login(f.email.trim().toLowerCase(), f.password);
        if (res?.success) nav('/app/dashboard');
        else toast.error(res?.error || "That email and password don't match");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail
        || 'That did not go through');
    } finally { setBusy(false); }
  };

  const ready = joining
    ? f.full_name.trim() && f.email.includes('@') && f.password.length >= 8 && f.date_of_birth
    : f.email.includes('@') && f.password;

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="tenant-auth-page">
      {/* Their side */}
      <div className="relative flex flex-col justify-center px-8 md:px-14 py-14 overflow-hidden"
        style={{ background: P.primary, color: P.onPrimary }}>
        <div className="absolute inset-0 opacity-[0.16] pointer-events-none"
          style={{ background: `radial-gradient(60% 50% at 20% 20%, ${P.accent} 0%, transparent 60%),
                                radial-gradient(50% 50% at 85% 80%, ${P.secondary} 0%, transparent 65%)` }} />
        <div className="relative">
          <LogoBox src={b.logo_url} name={name} size="lg" tone="dark" />
          <h1 className="font-outfit text-3xl md:text-4xl font-black tracking-tighter mt-9 leading-[1.05]">
            {joining ? `You're joining ${name}.` : `Welcome back to ${name}.`}
          </h1>
          <p className="mt-5 text-base leading-relaxed opacity-90 max-w-md">
            {joining
              ? 'Next you\'ll meet your FuturePath, a guided look at what fits you, what the life you want costs, and the routes that get you there.'
              : 'Pick up exactly where you left off.'}
          </p>
          {joining && f.code && (
            <p className="mt-7 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md
                          border border-white/25 px-4 py-2 text-sm font-mono" data-testid="tenant-auth-code">
              code {f.code}
            </p>
          )}
          <Link to={`/${slug}`} data-testid="tenant-auth-back"
            className="mt-10 inline-flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100 transition-opacity">
            <ArrowLeft className="w-4 h-4" /> back to {name}
          </Link>
        </div>
      </div>

      {/* The form */}
      <div className="flex items-center justify-center px-6 md:px-14 py-14">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm">
          <h2 className="font-outfit text-2xl font-bold tracking-tight text-slate-900">
            {joining ? 'Create your account' : 'Sign in'}
          </h2>
          <p className="text-sm text-slate-300 mt-2">
            {joining ? 'Two minutes, then you\'re in.' : 'With the email you signed up with.'}
          </p>

          <div className="space-y-4 mt-8">
            {joining && (
              <>
                <Field label="Your full name" value={f.full_name} testid="tenant-name"
                  onChange={v => setF({ ...f, full_name: v })} />
                <Field label="Date of birth" type="date" value={f.date_of_birth} testid="tenant-dob"
                  onChange={v => setF({ ...f, date_of_birth: v })} />
              </>
            )}
            <Field label="Email" type="email" value={f.email} testid="tenant-email"
              onChange={v => setF({ ...f, email: v })} />
            <Field label={joining ? 'Choose a password' : 'Password'} type="password" testid="tenant-password"
              value={f.password} onChange={v => setF({ ...f, password: v })} />
            {joining && (
              <Field label="Join code (optional)" value={f.code} testid="tenant-code"
                onChange={v => setF({ ...f, code: v.toUpperCase() })} />
            )}
          </div>

          <button onClick={submit} disabled={busy || !ready} data-testid="tenant-auth-submit"
            className="w-full h-12 mt-8 rounded-full font-bold text-sm inline-flex items-center justify-center gap-2
                       transition-transform hover:scale-[1.01] disabled:opacity-45"
            style={{ background: P.primary, color: P.onPrimary }}>
            {busy ? 'One moment…' : joining ? <>Start my FuturePath <ArrowRight className="w-4 h-4" /></> : 'Sign in'}
          </button>

          <p className="text-sm text-slate-300 mt-6">
            {joining
              ? <>Already have an account? <Link to={`/${slug}/signin`} data-testid="tenant-to-signin"
                  className="font-semibold" style={{ color: P.ink }}>Sign in</Link></>
              : <>New here? <Link to={`/${slug}/join`} data-testid="tenant-to-join"
                  className="font-semibold" style={{ color: P.ink }}>Join with your code</Link></>}
          </p>
          {joining && (
            <p className="text-xs text-slate-300 mt-6 flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Under 13? A parent or guardian sets this up for you, ask them to start it.
            </p>
          )}
          <p className="text-xs text-slate-300 mt-8">
            {b.powered_by_text || 'Powered by Agentic FuturePath AI'}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">{label}</span>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      className="mt-2 h-12 w-full rounded-2xl border-2 border-slate-200 px-4 text-slate-900
                 focus:outline-none transition-colors"
      style={{ borderColor: value ? 'var(--tenant-primary)' : undefined }} />
  </label>
);
